import { useEffect, useState } from "react";
import { Sparkles, RefreshCw, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * "Tip of the Day" — a 60-120 word actionable idea tailored to the student's
 * weakest subject, learning style, WRAT-5 scores, and interests. Cached for 24h
 * on the backend so opens are instant.
 */
export default function TipOfTheDay() {
  const [tip, setTip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchTip = async (refresh = false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      const { data } = await api.get(`/tips/of-the-day${refresh ? "?refresh=true" : ""}`);
      setTip(data);
    } catch (e) {
      // 404 = no student yet. Silent.
      if (e.response?.status !== 404) {
        toast.error("Could not load today's tip");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchTip(false); }, []);

  if (loading) {
    return (
      <div className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50 p-5 text-sm text-violet-800 flex items-center gap-2" data-testid="tip-of-day-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Brewing today's tip…
      </div>
    );
  }
  if (!tip) return null;

  return (
    <div className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50 p-5 shadow-sm" data-testid="tip-of-day-card">
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs uppercase tracking-[0.18em] font-semibold text-violet-700 flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> Tip of the day &middot; for {tip.student_name}
        </div>
        <button
          onClick={() => fetchTip(true)}
          disabled={refreshing}
          className="text-violet-600 hover:text-violet-800 disabled:opacity-40"
          title="Regenerate today's tip"
          data-testid="tip-of-day-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>
      <h3 className="mt-2 font-display text-lg font-semibold leading-snug text-stone-900" data-testid="tip-of-day-headline">
        {tip.headline}
      </h3>
      <p className="mt-2 text-sm text-stone-700 leading-relaxed" data-testid="tip-of-day-body">{tip.body}</p>
      {(tip.focus_subject || tip.why) && (
        <div className="mt-3 flex items-center flex-wrap gap-2 text-xs">
          {tip.focus_subject && (
            <span className="inline-flex items-center rounded-full bg-white/70 border border-violet-200 text-violet-700 px-2 py-0.5 font-semibold">
              {tip.focus_subject}
            </span>
          )}
          {tip.why && <span className="text-stone-500">{tip.why}</span>}
        </div>
      )}
    </div>
  );
}
